import {
  forwardRef,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseTabsItem, BaseTabsProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

function validateTabsProps({
  items,
  value,
  defaultValue,
  orientation,
  activationMode,
}: Pick<
  BaseTabsProps,
  'activationMode' | 'defaultValue' | 'items' | 'orientation' | 'value'
>) {
  if (!Array.isArray(items)) {
    throw new TypeError('BaseTabs items must be an array.');
  }
  if (value !== undefined && typeof value !== 'string') {
    throw new TypeError('BaseTabs value must be a string.');
  }
  if (defaultValue !== undefined && typeof defaultValue !== 'string') {
    throw new TypeError('BaseTabs defaultValue must be a string.');
  }
  if (orientation !== 'horizontal' && orientation !== 'vertical') {
    throw new TypeError(
      'BaseTabs orientation must be "horizontal" or "vertical".',
    );
  }
  if (activationMode !== 'automatic' && activationMode !== 'manual') {
    throw new TypeError(
      'BaseTabs activationMode must be "automatic" or "manual".',
    );
  }

  const values = new Set<string>();
  items.forEach((item, index) => {
    if (!item || typeof item !== 'object') {
      throw new TypeError(`BaseTabs item at index ${index} must be an object.`);
    }
    if (typeof item.value !== 'string') {
      throw new TypeError(
        `BaseTabs item value at index ${index} must be a string.`,
      );
    }
    if (values.has(item.value)) {
      throw new TypeError(
        'BaseTabs items must use unique values; ' +
          `duplicate value ${JSON.stringify(item.value)} at index ${index}.`,
      );
    }
    if (item.disabled !== undefined && typeof item.disabled !== 'boolean') {
      throw new TypeError(
        `BaseTabs item disabled at index ${index} must be a boolean.`,
      );
    }
    values.add(item.value);
  });
}

function getResolvedValue(items: BaseTabsItem[], candidate?: string) {
  if (
    candidate !== undefined &&
    items.some((item) => item.value === candidate && !item.disabled)
  ) {
    return candidate;
  }
  return items.find((item) => !item.disabled)?.value;
}

function toDomId(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, '-');
}

function getDomIdFragments(items: BaseTabsItem[]) {
  const usedFragments = new Set<string>();

  return items.map((item, index) => {
    const baseFragment = toDomId(item.value);
    let fragment = baseFragment;

    if (usedFragments.has(fragment)) {
      fragment = `${baseFragment}-${index}`;
      while (usedFragments.has(fragment)) {
        fragment = `${fragment}-${index}`;
      }
    }

    usedFragments.add(fragment);
    return fragment;
  });
}

function getHorizontalStep(
  event: KeyboardEvent<HTMLButtonElement>,
  key: 'ArrowLeft' | 'ArrowRight',
) {
  const view = event.currentTarget.ownerDocument.defaultView;
  const direction = view?.getComputedStyle(event.currentTarget).direction;
  const rtl = direction === 'rtl';

  if (key === 'ArrowRight') return rtl ? -1 : 1;
  return rtl ? 1 : -1;
}

export const BaseTabs = forwardRef<HTMLDivElement, BaseTabsProps>(
  function BaseTabs(
    {
      items = [],
      value,
      defaultValue,
      orientation = 'horizontal',
      activationMode = 'automatic',
      appearance,
      className,
      classNames,
      style,
      onValueChange,
      id,
      'aria-label': tablistAriaLabel,
      'aria-labelledby': tablistAriaLabelledBy,
      ...tabsProps
    },
    ref,
  ) {
    validateTabsProps({
      items,
      value,
      defaultValue,
      orientation,
      activationMode,
    });

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const generatedId = useId();
    const rootId = id ?? generatedId;
    const initialCandidate = value !== undefined ? value : defaultValue;
    const [uncontrolledValue, setUncontrolledValue] = useState(() =>
      getResolvedValue(items, defaultValue),
    );
    const [focusedValue, setFocusedValue] = useState(() =>
      getResolvedValue(items, initialCandidate),
    );
    const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const isControlled = value !== undefined;
    const selectedValue = getResolvedValue(
      items,
      isControlled ? value : uncontrolledValue,
    );
    const selectedIndex = items.findIndex(
      (item) => item.value === selectedValue && !item.disabled,
    );
    const selectedItem = selectedIndex >= 0 ? items[selectedIndex] : undefined;
    const enabledIndices = items.reduce<number[]>((indices, item, index) => {
      if (!item.disabled) indices.push(index);
      return indices;
    }, []);
    const storedFocusIndex = items.findIndex(
      (item) => item.value === focusedValue && !item.disabled,
    );
    const rovingIndex =
      storedFocusIndex >= 0 ? storedFocusIndex : selectedIndex;
    const idFragments = getDomIdFragments(items);
    const groupState = selectedItem ? 'active' : 'disabled';
    const layerDirection =
      typeof tabsProps.dir === 'string'
        ? tabsProps.dir
        : componentAttributes.dir;

    const activateItem = (item: BaseTabsItem) => {
      if (item.disabled) return;
      if (!isControlled) {
        setUncontrolledValue(item.value);
      }
      onValueChange?.(item.value);
    };

    const moveFocus = (
      event: KeyboardEvent<HTMLButtonElement>,
      targetIndex: number,
    ) => {
      const targetItem = items[targetIndex];
      if (!targetItem || targetItem.disabled) return;

      event.preventDefault();
      setFocusedValue(targetItem.value);
      triggerRefs.current[targetIndex]?.focus();
      if (activationMode === 'automatic') {
        activateItem(targetItem);
      }
    };

    const handleKeyDown = (
      event: KeyboardEvent<HTMLButtonElement>,
      item: BaseTabsItem,
      index: number,
    ) => {
      if (item.disabled) return;

      if (
        activationMode === 'manual' &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        activateItem(item);
        return;
      }

      if (enabledIndices.length === 0) return;
      if (event.key === 'Home') {
        moveFocus(event, enabledIndices[0]);
        return;
      }
      if (event.key === 'End') {
        moveFocus(event, enabledIndices[enabledIndices.length - 1]);
        return;
      }

      let step: number | undefined;
      if (
        orientation === 'horizontal' &&
        (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
      ) {
        step = getHorizontalStep(event, event.key);
      } else if (orientation === 'vertical' && event.key === 'ArrowUp') {
        step = -1;
      } else if (orientation === 'vertical' && event.key === 'ArrowDown') {
        step = 1;
      }
      if (step === undefined) return;

      const enabledPosition = enabledIndices.indexOf(index);
      const nextPosition =
        (enabledPosition + step + enabledIndices.length) %
        enabledIndices.length;
      moveFocus(event, enabledIndices[nextPosition]);
    };

    return (
      <div
        {...componentAttributes}
        {...tabsProps}
        ref={ref}
        id={id}
        data-monkeys-component="tabs"
        data-slot="root"
        data-state={groupState}
        data-orientation={orientation}
        data-activation-mode={activationMode}
        className={cn(theme.slots.tabsRoot, classNames?.root, className)}
        style={style}
      >
        <div
          {...componentAttributes}
          dir={layerDirection}
          role="tablist"
          aria-label={tablistAriaLabel}
          aria-labelledby={tablistAriaLabelledBy}
          aria-orientation={orientation}
          data-monkeys-component="tabs"
          data-slot="list"
          data-state={groupState}
          data-orientation={orientation}
          data-activation-mode={activationMode}
          className={cn(
            theme.slots.tabsList,
            orientation === 'vertical' && 'flex-col items-stretch',
            classNames?.list,
          )}
          style={theme.styles.tabsList}
        >
          {items.map((item, index) => {
            const itemId = idFragments[index];
            const selected = index === selectedIndex;
            const state = item.disabled
              ? 'disabled'
              : selected
                ? 'active'
                : 'inactive';

            return (
              <button
                {...componentAttributes}
                ref={(node) => {
                  triggerRefs.current[index] = node;
                }}
                key={item.value}
                id={`${rootId}-${itemId}-tab`}
                type="button"
                role="tab"
                tabIndex={!item.disabled && index === rovingIndex ? 0 : -1}
                aria-selected={selected}
                aria-controls={`${rootId}-${itemId}-panel`}
                disabled={item.disabled}
                dir={layerDirection}
                data-monkeys-component="tabs"
                data-slot="trigger"
                data-state={state}
                data-orientation={orientation}
                data-activation-mode={activationMode}
                data-disabled={item.disabled || undefined}
                className={cn(
                  theme.slots.tabsTrigger,
                  selected && theme.slots.tabsTriggerActive,
                  item.disabled && theme.slots.tabsTriggerDisabled,
                  classNames?.trigger,
                )}
                style={theme.styles.tabsTrigger}
                onFocus={() => setFocusedValue(item.value)}
                onKeyDown={(event) => handleKeyDown(event, item, index)}
                onClick={() => {
                  setFocusedValue(item.value);
                  activateItem(item);
                }}
              >
                <span className="min-w-0 truncate">{item.label}</span>
              </button>
            );
          })}
        </div>

        {selectedItem ? (
          <div
            {...componentAttributes}
            id={`${rootId}-${idFragments[selectedIndex]}-panel`}
            role="tabpanel"
            tabIndex={0}
            aria-labelledby={`${rootId}-${idFragments[selectedIndex]}-tab`}
            dir={layerDirection}
            data-monkeys-component="tabs"
            data-slot="panel"
            data-state="active"
            data-orientation={orientation}
            data-activation-mode={activationMode}
            className={cn(theme.slots.tabsPanel, classNames?.panel)}
            style={theme.styles.tabsPanel}
          >
            {hasRenderableNode(selectedItem.content) ? (
              selectedItem.content
            ) : hasRenderableNode(selectedItem.description) ? (
              <p className="m-0 text-muted-foreground">
                {selectedItem.description}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  },
);
